import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Select, Switch } from "antd";
import { useSearchParams } from "react-router-dom";
import { svg } from "../../../../Assets/svg";
import { renderFormLabel } from "../../../../Components/Components";
import { useAppDispatch } from "../../../../store";
import GoogleAddressAutocomplete, {
  GoogleAddressResult,
} from "../../../../Components/GoogleAddressAutocomplete";
import {
  createClientDeliveryAddressAPI,
  updateClientDeliveryAddressAPI,
  getDeliveryZonePriceByPostcodeAPI,
  getDeliveryZoneForClientAPI,
} from "../clientDistributorsApis";
import { getDeliveryCountriesAPI } from "../../Quotations/quotationApis";
import { formatCurrency } from "../../../../helpers/utils";
import { useDebounce } from "../../../../helpers/utils";
import FullScreenLoader from "../../../Components/FullScreenLoader";

interface Props {
  open: boolean;
  onClose: () => void;
  clientId?: string | null;
  refreshData?: (newAddress?: any) => void;
  addressData?: any;
  isDistributor?: boolean;
  onSubmitOverride?: (payload: any, setSaving: (val: boolean) => void) => void;
  quoteData?: any;
  currency?: string;
  conversion_rate?: number;
}

export const ModifyDeliveryAddressModal: React.FC<Props> = ({
  open,
  onClose,
  clientId,
  refreshData,
  addressData,
  isDistributor = false,
  onSubmitOverride,
  quoteData = null,
  currency,
  conversion_rate,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const deliveryCountryId = Form.useWatch("delivery_country_id", form);
  const zipCode = Form.useWatch("zip", form);
  const isCrossBorder = Form.useWatch("is_cross_border", form);
  const areaCode = Form.useWatch("area_id", form);

  const [prefixData, setPrefixData] = useState<any[]>([]);
  const [prefixCount, setPrefixCount] = useState<number>(0);

  const [countries, setCountries] = useState<any[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<
    string[] | undefined
  >();
  const [deliveryZoneInfo, setDeliveryZoneInfo] = useState<any>(null);
  const [countriesLoading, setCountriesLoading] = useState<boolean>(false);

  const [zonesLoading, setZonesLoading] = useState<boolean>(false);
  const [areaSearch, setAreaSearch] = useState("");
  const debouncedAreaSearch = useDebounce(areaSearch, 300);
  const [currentPage, setCurrentPage] = useState(1);

  const zones = React.useMemo(() => {
    const list = (prefixData || []).map((item: any) => ({
      value: item._id || item.area_id,
      label: item?.prefix
        ? `${item.area_name} (${item?.prefix})`
        : `${item.area_name}`,
      item: item,
    }));

    let injectedValue: any = null;
    let injectedLabel: string = "";
    let injectedItem: any = null;

    if (addressData?.area_id && typeof addressData.area_id === "object") {
      injectedValue = addressData.area_id._id || addressData.area_id.area_id;
      injectedLabel = addressData.area_id.prefix
        ? `${addressData.area_id.area_name} (${addressData.area_id.prefix})`
        : `${addressData.area_id.area_name}`;
      injectedItem = addressData.area_id;
    } else if (deliveryZoneInfo && areaCode) {
      injectedValue = areaCode;
      injectedLabel = deliveryZoneInfo.prefix
        ? `${deliveryZoneInfo.area_name} (${deliveryZoneInfo.prefix})`
        : `${deliveryZoneInfo.area_name}`;
      injectedItem = deliveryZoneInfo;
    }

    if (injectedValue) {
      const exists = list.find((item: any) => item.value === injectedValue);
      if (!exists && injectedLabel && injectedLabel !== "undefined") {
        list.unshift({
          value: injectedValue,
          label: injectedLabel,
          item: injectedItem,
        });
      }
    }

    return list;
  }, [prefixData, addressData, deliveryZoneInfo, areaCode]);

  const [coordinates, setCoordinates] = useState<{
    lat: number | null;
    long: number | null;
  }>({ lat: null, long: null });

  useEffect(() => {
    if (!deliveryCountryId || (!zipCode && !areaCode)) {
      setDeliveryZoneInfo(null);
      return;
    }

    const selectedZoneItem = zones?.find(
      (item: any) => item?.value === areaCode,
    )?.item;

    let areaIds = selectedZoneItem?.prefixes?.map((pf: any) => pf?._id);

    if (!areaIds && selectedZoneItem?._id) {
      areaIds = [selectedZoneItem._id];
    }

    if ((!areaIds || areaIds.length === 0)&&!zipCode) return;

    setDeliveryZoneInfo(null);
    const timer = setTimeout(() => {
      dispatch(
        getDeliveryZonePriceByPostcodeAPI({
          payload: {
            country_id: deliveryCountryId,
            post_code: zipCode ?? null,
            cross_border: !!isCrossBorder,
            area_id: areaIds ?? null,
          },
          onSuccess: (data: any) => {
            setDeliveryZoneInfo(data);
          },
          onError: () => {
            setDeliveryZoneInfo(null);
          },
        }),
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [deliveryCountryId, zipCode, isCrossBorder, areaCode, dispatch]);

  useEffect(() => {
    const getCountries = () => {
      setCountriesLoading(true);
      dispatch(
        getDeliveryCountriesAPI({
          onSuccess: (response) => {
            const data = response?.length > 0 ? response : [];
            setCountries(data);
            setCountriesLoading(false);
          },
          onError: () => {
            setCountriesLoading(false);
          },
        }),
      );
    };
    getCountries();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedAreaSearch, deliveryCountryId]);

  useEffect(() => {
    if (deliveryCountryId) {
      setZonesLoading(true);
      dispatch(
        getDeliveryZoneForClientAPI({
          country_id: deliveryCountryId,
          search: debouncedAreaSearch,
          page: currentPage,
          limit: 25,
          onSuccess: (data: any) => {
            const list = data?.list || data || [];
            const count = data?.count || list.length;
            if (currentPage === 1) {
              setPrefixData(list);
            } else {
              setPrefixData((prev) => [...prev, ...list]);
            }
            setPrefixCount(count);
            setZonesLoading(false);
          },
          onError: () => {
            setZonesLoading(false);
          },
        }),
      );
    } else {
      setPrefixData([]);
      setPrefixCount(0);
    }
  }, [deliveryCountryId, debouncedAreaSearch, currentPage, dispatch]);

  useEffect(() => {
    if (open) {
      if (addressData) {
        form.setFieldsValue({
          streetAddress: addressData.address,
          googleLocation: addressData.address,
          country: addressData.country,
          state: addressData.state,
          city: addressData.city,
          zip: addressData.zipcode,
          area_id: addressData.area_id?._id ?? null,
          delivery_country_id:
            addressData?.delivery_country_id?._id ?? undefined,
          is_cross_border: !!addressData.is_cross_border,
        });
        setCoordinates({
          lat: addressData.lat || null,
          long: addressData.lng || null,
        });
      } else {
        form.resetFields();
        setCoordinates({ lat: null, long: null });
        setSelectedCountryCode(undefined);
        setDeliveryZoneInfo(null);
      }
    }
  }, [open, addressData, form]);

  const handlePlaceSelect = (place: GoogleAddressResult) => {
    setCoordinates({
      lat: place.lat,
      long: place.lng,
    });

    form.setFieldsValue({
      streetAddress: place.address || "",
      country: place.country || "",
      state: place.state || "",
      city: place.city || "",
      zip: place.zip || "",
    });
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload: any = quoteData
        ? {
            quote_id: quoteData?.quote_id,
            delivery_address: {
              address: values.streetAddress ?? null,
              country: values.country ?? null,
              state: values.state ?? null,
              city: values.city ?? null,
              zipcode: values.zip ?? null,
              delivery_country_id: values.delivery_country_id,
              area_id: values.area_id ?? null,
              is_cross_border: !!values.is_cross_border,
              lat: coordinates.lat ?? null,
              lng: coordinates.long ?? null,
            },
          }
        : {
            ...(clientId ? { customer_id: clientId } : {}),
            address: values.streetAddress ?? null,
            country: values.country ?? null,
            state: values.state ?? null,
            city: values.city ?? null,
            zipcode: values.zip ?? null,
            area_id: values.area_id ?? null,
            lat: coordinates.lat ?? null,
            lng: coordinates.long ?? null,
            delivery_country_id: values.delivery_country_id,
            is_cross_border: !!values.is_cross_border,
          };

      if (onSubmitOverride) {
        onSubmitOverride(payload, setSaving);
        return;
      }

      if (clientId) {
        // --- EXISTING CLIENT: USE API ---
        const commonCallbacks = {
          onSuccess: (data: any) => {
            setSaving(false);
            onClose();
            refreshData?.(data);
          },
          onError: () => {
            setSaving(false);
          },
        };

        if (addressData) {
          payload.id = addressData._id;
          dispatch(
            updateClientDeliveryAddressAPI({
              payload: payload,
              params: contextualParams,
              ...commonCallbacks,
            }),
          );
        } else {
          dispatch(
            createClientDeliveryAddressAPI({
              payload: payload,
              params: contextualParams,
              ...commonCallbacks,
            }),
          );
        }
      } else {
        // --- NEW CLIENT (LOCAL): RETURN OBJECT IMMEDIATELY ---
        const tempAddress = {
          ...payload,
          delivery_country_id: payload.delivery_country_id
            ? { _id: payload.delivery_country_id }
            : null,
          area_id: payload.area_id ? { _id: payload.area_id } : null,
          _id: addressData?._id || `temp_${Date.now()}`,
          isLocal: true,
        };

        setTimeout(() => {
          setSaving(false);
          refreshData?.(tempAddress);
          onClose();
        }, 300);
      }
    } catch (error) {
      console.log("Validation Failed:", error);
      setSaving(false);
    }
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width={"35rem"}
      className="commonModalClass"
      wrapClassName="zn-1051"
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          {addressData ? "Update Delivery Address" : "Add Delivery Address"}
        </div>
      }
    >
      {countriesLoading ? (
        <div className="p-5">
          <FullScreenLoader />
        </div>
      ) : (
        <Form
          layout="vertical"
          form={form}
          className="page-form"
          onFinish={onSave}
        >
          {quoteData && Object.keys(quoteData).length > 0 && (
            <div className="text-secondary fw-semibold mb-3 text-center">
              Please complete the delivery address details to proceed with order
              creation.
            </div>
          )}
          <Form.Item
            label={renderFormLabel("Delivery Country", false)}
            name="delivery_country_id"
            required={false}
            rules={[
              { required: true, message: "Delivery Country is required" },
            ]}
          >
            <Select
              showSearch
              placeholder="Delivery Country"
              // className="form-control form-control-lg"
              suffixIcon={svg?.selectDownArrow}
              filterOption={(input, option: any) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={countries?.map((item: any) => ({
                ...item,
                value: item?._id,
                label: item?.country_name,
              }))}
              onChange={(value, option: any) => {
                if (value !== deliveryCountryId) {
                  form.setFieldsValue({
                    area_id: undefined,
                    googleLocation: undefined,
                    streetAddress: undefined,
                    state: undefined,
                    city: undefined,
                    zip: undefined,
                  });
                  setCoordinates({ lat: null, long: null });
                }

                if (option?.google_country_code) {
                  setSelectedCountryCode([option.google_country_code]);
                } else {
                  setSelectedCountryCode(undefined);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={renderFormLabel("Google Location", false)}
            name="googleLocation"
            required={false}
          >
            <GoogleAddressAutocomplete
              placeholder="Search Google Location"
              onPlaceSelect={handlePlaceSelect}
              countryCode={selectedCountryCode}
              disabled={!deliveryCountryId}
            />
          </Form.Item>

          <Form.Item
            label={renderFormLabel("Street Address", false)}
            name="streetAddress"
            rules={[
              {
                required: isDistributor ? false : true,
                message: "Street Address is required",
              },
              { whitespace: true, message: "Cannot be empty" },
            ]}
            required={false}
          >
            <Input
              placeholder="Enter Address"
              className="form-control form-control-lg"
              disabled={!deliveryCountryId}
            />
          </Form.Item>

          <div className="row gy-4">
            {/* <div className="col-md-6">
              <Form.Item
                label={renderFormLabel("Country", false)}
                name="country"
                required={false}
                rules={[
                  { required: true, message: "Country is required" },
                  { whitespace: true, message: "Cannot be empty" },
                ]}
              >
                <Input
                  placeholder="Country"
                  className="form-control form-control-lg"
                  disabled={!deliveryCountryId}
                />
              </Form.Item>
            </div> */}

            <div className="col-md-6">
              <Form.Item
                label={renderFormLabel("State", false)}
                name="state"
                required={false}
                rules={[
                  {
                    required: isDistributor ? false : true,
                    message: "State is required",
                  },
                  { whitespace: true, message: "Cannot be empty" },
                ]}
              >
                <Input
                  placeholder="State"
                  className="form-control form-control-lg"
                  disabled={!deliveryCountryId}
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item
                label={renderFormLabel("City", false)}
                name="city"
                required={false}
                rules={[
                  {
                    required: isDistributor ? false : true,
                    message: "City is required",
                  },
                  { whitespace: true, message: "Cannot be empty" },
                ]}
              >
                <Input
                  placeholder="City"
                  className="form-control form-control-lg"
                  disabled={!deliveryCountryId}
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item
                label={renderFormLabel("Area Name", false)}
                name="area_id"
                required={false}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const zip = getFieldValue("zip");
                      if (value || (zip && zip.trim())) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Area Name is required when Postal Code is empty"),
                      );
                    },
                  }),
                ]}
              >
                <Select
                  popupClassName="full-text-select-dropdown"
                  showSearch
                  placeholder="Select Area"
                  suffixIcon={svg?.selectDownArrow}
                  options={zones}
                  disabled={!deliveryCountryId}
                  onSearch={(val) => setAreaSearch(val)}
                  onChange={(val) => {
                    form.setFieldValue("area_id", val);
                  }}
                  onClear={() => setAreaSearch("")}
                  filterOption={false}
                  onPopupScroll={(e) => {
                    const { target } = e as any;
                    if (
                      target.scrollTop + target.offsetHeight >=
                      target.scrollHeight - 10
                    ) {
                      if ((prefixData || []).length < prefixCount) {
                        setCurrentPage((prev) => prev + 1);
                      }
                    }
                  }}
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item
                label={renderFormLabel("Postal Code", false)}
                name="zip"
                required={false}
                dependencies={["area_id"]}
              >
                <Input
                  placeholder="Postal Code"
                  className="form-control form-control-lg"
                  disabled={!deliveryCountryId}
                />
              </Form.Item>
            </div>
          </div>

          {/* <div className="d-flex align-items-center gap-2 mt-2">
          <span className="fw-semibold">Is this cross border delivery?</span>
          <Form.Item
            name="is_cross_border"
            valuePropName="checked"
            required={false}
            className="mb-0"
          >
            <Switch
              className="customSwitch"
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>
        </div> */}

          {deliveryZoneInfo && (
            <div className="bg-light p-3 rounded mb-4 mt-3 border border-secondary-subtle">
              <h6 className="mb-3 fw-bold text-dark">Delivery Zone Details</h6>

              <div className="row gy-2 text-secondary mb-2">
                <div className="col-md-6">
                  <strong>Zone:</strong> {deliveryZoneInfo.zone_name}
                </div>
                <div className="col-md-6">
                  <strong>Prefix:</strong> {deliveryZoneInfo.area_name} (
                  {deliveryZoneInfo.prefix}){isCrossBorder && " (Cross Border)"}
                </div>
              </div>

              {deliveryZoneInfo.markup_price && (
                <div className="row gy-2 text-secondary">
                  <div className="col-md-4">
                    <strong>Pallet Price:</strong>{" "}
                    {formatCurrency(
                      deliveryZoneInfo.markup_price.pallet_price,
                      currency || quoteData?.currency,
                      conversion_rate || quoteData?.conversion_rate,
                    )}
                  </div>
                  <div className="col-md-4">
                    <strong>Half Trailer:</strong>{" "}
                    {formatCurrency(
                      deliveryZoneInfo.markup_price.tailer?.half_price,
                      currency || quoteData?.currency,
                      conversion_rate || quoteData?.conversion_rate,
                    )}
                  </div>
                  <div className="col-md-4">
                    <strong>Full Trailer:</strong>{" "}
                    {formatCurrency(
                      deliveryZoneInfo.markup_price.tailer?.full_price,
                      currency || quoteData?.currency,
                      conversion_rate || quoteData?.conversion_rate,
                    )}
                  </div>
                  <div className="col-md-6">
                    <strong>Mezzanine Half Trailer:</strong>{" "}
                    {formatCurrency(
                      deliveryZoneInfo.markup_price.mezzanine_tailer
                        ?.mezzanine_half_price ??
                        deliveryZoneInfo.markup_price.mezzanine_half_price,
                      currency || quoteData?.currency,
                      conversion_rate || quoteData?.conversion_rate,
                    )}
                  </div>
                  <div className="col-md-6">
                    <strong>Mezzanine Full Trailer:</strong>{" "}
                    {formatCurrency(
                      deliveryZoneInfo.markup_price.mezzanine_tailer
                        ?.mezzanine_full_price ??
                        deliveryZoneInfo.markup_price.mezzanine_full_price,
                      currency || quoteData?.currency,
                      conversion_rate || quoteData?.conversion_rate,
                    )}
                  </div>
                </div>
              )}

              {(deliveryZoneInfo?.markup_box_prices ||
                deliveryZoneInfo?.box_prices) &&
                (deliveryZoneInfo.markup_box_prices ||
                  deliveryZoneInfo.box_prices).length > 0 && (
                  <div className="mt-3 pt-2 border-top border-secondary-subtle">
                    <strong className="d-block mb-2 text-dark">
                      Parcel Delivery Prices
                    </strong>
                    <div className="row gy-2 text-secondary">
                      {(
                        deliveryZoneInfo.markup_box_prices ||
                        deliveryZoneInfo.box_prices
                      ).map((box: any, index: number) => (
                        <div
                          className="col-md-6 col-sm-6 col-12"
                          key={box.box_id || box._id || index}
                        >
                          <strong>{box.name}:</strong>{" "}
                          {formatCurrency(
                            box.price,
                            currency || quoteData?.currency,
                            conversion_rate || quoteData?.conversion_rate,
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              onClick={onClose}
              className="btn btn-lg btn-outline-secondary h-auto px-5"
              type="default"
            >
              CLOSE
            </Button>

            <Button
              onClick={onSave}
              loading={saving}
              className="btn btn-lg btn-dark h-auto px-5"
            >
              {addressData ? "UPDATE" : "SAVE"}
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};
